/**
 * lib/customerLink.ts
 * One customer, one row: tie each ticket to the contact record it was raised
 * for, and build the create-ticket request from the contact that was picked.
 *
 * Until now customers were keyed by email alone. A ticket's customer was a
 * name guessed from its email ("ganesh.hw.qa@…" → "Ganesh Hw Qa"), and the
 * Contacts list was the API contacts plus one more row per ticket email — so
 * an SK-CRM customer with only a phone ("Ganesh Hardware") showed twice, with
 * his ticket on the made-up row, and could not be picked for a new ticket at
 * all (dev, 28 Oct 2026). A ticket already stores the contact it belongs to
 * (`requesterId`: ctc_… for SK-CRM, cid_… for a local caller); that id is the
 * key, and email is only the fallback for contacts the API did not return.
 */

/** The fields of a contact or ticket customer this module reads. */
export interface LinkableCustomer {
  _contactId?: string | null;
  name: string;
  email: string;
  phone?: string;
  org?: string;
  initials?: string;
}

const lower = (s: string | null | undefined) => (s || '').trim().toLowerCase();

/** A stable key for a customer: the contact id when known, else the email. */
export function customerKey(c: {
  _contactId?: string | null;
  email?: string | null;
}): string {
  return c._contactId ? `id:${c._contactId}` : `email:${lower(c.email)}`;
}

/**
 * Point each ticket's customer at the contact record it belongs to — by the
 * ticket's `requesterId`, else by email — and show that contact's name,
 * phone and company. A ticket with no matching contact keeps what it had.
 */
export function linkTicketCustomers<
  T extends { requesterId?: string | null; customer: LinkableCustomer },
>(tickets: T[], contacts: LinkableCustomer[]): T[] {
  const byId = new Map<string, LinkableCustomer>();
  const byEmail = new Map<string, LinkableCustomer>();
  for (const c of contacts) {
    if (c._contactId) {
      byId.set(c._contactId, c);
    }
    if (c.email) {
      byEmail.set(lower(c.email), c);
    }
  }
  return tickets.map(t => {
    const c =
      (t.requesterId && byId.get(t.requesterId)) ||
      byEmail.get(lower(t.customer.email));
    if (!c) {
      return t;
    }
    return {
      ...t,
      customer: {
        ...t.customer,
        _contactId: c._contactId ?? t.customer._contactId ?? null,
        name: c.name || t.customer.name,
        // A phone-only contact has no email: keep the ticket's own reply-to.
        email: c.email || t.customer.email,
        phone: c.phone || t.customer.phone,
        org: c.org || t.customer.org,
        initials: c.initials || t.customer.initials,
      },
    };
  });
}

/**
 * The API contacts, then the ticket customers that match none of them. A
 * ticket customer matches a contact by id or by email; a matched contact keeps
 * its own name, email and phone and takes the rest (its ticket count) from the
 * ticket customer.
 */
export function mergeContacts<C extends LinkableCustomer>(
  apiContacts: C[],
  ticketCustomers: C[],
): C[] {
  const matches = (api: C, t: C) =>
    (Boolean(api._contactId) && t._contactId === api._contactId) ||
    (Boolean(api.email) && lower(t.email) === lower(api.email));
  const used = new Set<C>();
  const merged = apiContacts.map(api => {
    const t = ticketCustomers.find(x => !used.has(x) && matches(api, x));
    if (!t) {
      return api;
    }
    used.add(t);
    return { ...t, ...api };
  });
  return [
    ...merged,
    ...ticketCustomers.filter(
      t => !used.has(t) && !apiContacts.some(api => matches(api, t)),
    ),
  ];
}

/** What the New ticket form hands to addTicket. */
export interface NewTicketInput {
  subject: string;
  channel?: string;
  priority?: string;
  category?: string;
  body?: string;
  customerId?: string | null;
  customerEmail?: string;
  customerPhone?: string | null;
  customerName?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const E164_RE = /^\+[1-9]\d{6,14}$/;

/**
 * The POST /v1/tickets body. A picked SK-CRM contact (ctc_…) is sent as the
 * requester, so the ticket belongs to that contact instead of a new local
 * caller; its phone goes too, because phone is the CRM key. Invalid email or
 * phone values are left out rather than sent for the API to refuse.
 */
export function buildTicketPayload(
  data: NewTicketInput,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    subject: data.subject,
    source:
      data.channel === 'email'
        ? 'email'
        : data.channel === 'api'
        ? 'api'
        : 'manual',
    priority: data.priority || 'medium',
  };
  if (data.customerId && data.customerId.startsWith('ctc_')) {
    payload.requesterId = data.customerId;
  }
  const email = (data.customerEmail ?? '').trim();
  if (email && EMAIL_RE.test(email)) {
    payload.requesterEmail = email;
  }
  const phone = (data.customerPhone ?? '').trim();
  if (phone && E164_RE.test(phone)) {
    payload.callerPhone = phone;
  }
  if (data.customerName) {
    payload.callerName = data.customerName;
  }
  if (data.body) {
    payload.description = data.body;
  }
  if (data.category) {
    payload.category = data.category;
  }
  return payload;
}
